/**
 * Which tab hosts a workspace. The server elects the user's host (C5, role.ts): the elected tab
 * takes the lease, and the others stand by while it is elected (`sandbox.host` on_device, this tab
 * standby), trying again while calls wait, so the tab promoted next takes over. While a hosted
 * executor serves the calls (kind hosted, `served`) every tab stands by: a tab that took the lease
 * the hosted pool just lost could start a call the pool already took (C13's taken marker). With no
 * tab elected and none served (no frame yet, or hosted on the web without a hosted executor) the
 * first tab to take the lease holds it, as before election, and runs the calls. A tab whose sandbox
 * failed to start (markBootFailed) leaves each lease to the others for a grace first
 * (BOOT_FAILED_GRACE_MS).
 */
import { LeaseError } from "../sync/api";
import { bootFailed } from "./candidate";
import { openHostSession, type HostSession } from "../sync/session";
import type { SandboxHost } from "./role";

/** This tab's session for the workspace, or null when another host has it. */
export type HostPolicy = (workspaceId: string, owner: string) => Promise<HostSession | null>;

export function firstToAcquire(open: typeof openHostSession = openHostSession): HostPolicy {
  return async (workspaceId, owner) => {
    try {
      return await open(workspaceId, owner);
    } catch (error) {
      if (error instanceof LeaseError && error.code === "lease_held") return null;
      throw error;
    }
  };
}

/**
 * How long a tab whose sandbox failed to start (markBootFailed) leaves a workspace's lease to the
 * other tabs, from its first refusal of it: two heartbeats (HEARTBEAT_MS), so a healthy tab trying
 * meanwhile takes it. Past it this tab takes the lease and answers the calls at once (its executor
 * will not start: "reload the page"), not at their deadlines.
 */
export const BOOT_FAILED_GRACE_MS = 10_000;

/** Whether this tab's part (`sandbox.host`) keeps it off the leases: another tab or a hosted
 * executor is the host. Null (no frame yet) does not. */
export function standsBy(host: SandboxHost | null): boolean {
  return host?.role === "standby" && (host.kind === "on_device" || !!host.served);
}

/**
 * First to acquire, unless another tab or a hosted executor is the host: `role` is this tab's part
 * now, read again once the lease is taken, since it can turn while that is in flight. A lease taken
 * by a tab that stands by by then is given straight back.
 */
export function electedPolicy(
  role: () => SandboxHost | null,
  open: typeof openHostSession = openHostSession,
  now: () => number = Date.now
): HostPolicy {
  const acquire = firstToAcquire(open);
  /** When this tab, its sandbox failed, first left each workspace's lease to the others. */
  const leftSince = new Map<string, number>();
  const leaves = (workspaceId: string) => {
    if (!bootFailed()) return false;
    if (!leftSince.has(workspaceId)) leftSince.set(workspaceId, now());
    return now() - leftSince.get(workspaceId)! < BOOT_FAILED_GRACE_MS;
  };
  return async (workspaceId, owner) => {
    if (standsBy(role()) || leaves(workspaceId)) return null;
    const session = await acquire(workspaceId, owner);
    if (session && standsBy(role())) {
      await session.release().catch(() => undefined);
      return null;
    }
    return session;
  };
}
